var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/** @jsx jsx */
import { React, jsx, classNames, css, ReactRedux, lodash, hooks } from 'jimu-core';
import { animate } from './animate';
import { getNormalizedScrollLeft } from './scroll-left';
const { useRef, useMemo, useImperativeHandle, useState, useEffect } = React;
const { useSelector } = ReactRedux;
const scrollStyle = css `
  overflow-x: scroll;
  /* Firefox + Chrome */
  scrollbar-width: none;
  /* Safari */
  &::-webkit-scrollbar {
    display: none;
  }

  /* for edge */
  -ms-overflow-style: none;
`;
const useStyle = (horizontal) => {
    return useMemo(() => {
        return css `
      overflow: hidden;
      min-height: 20px;
      width: 100%;
      height: 100%;
      /* Add iOS momentum scrolling. */
      -webkit-overflow-scrolling: touch;
      .scroll-root {
        width: ${horizontal ? '100%' : 'unset'};
        height: ${!horizontal ? '100%' : 'unset'};
        white-space: nowrap;
        ${scrollStyle};
        > .scroll-container {
          min-width: 100%;
          min-height: 100%;
          width: fit-content;
        }
      }
    `;
    }, [horizontal]);
};
/**
 * The `Scrollable` provides users with the ability to scroll through children without displaying the scroll bar.
 *
 * ```ts
 * import { Scrollable } from 'jimu-ui'
 * ```
 */
export const Scrollable = React.forwardRef((props, ref) => {
    const { className, horizontal = false, children, version } = props, others = __rest(props, ["className", "horizontal", "children", "version"]);
    const style = useStyle(horizontal);
    const isRTL = useSelector((state) => state.appContext.isRTL);
    const scrollStart = !horizontal ? 'scrollTop' : 'scrollLeft';
    const clientSize = !horizontal ? 'clientHeight' : 'clientWidth';
    const rootRef = useRef(null);
    const [topping, setTopping] = useState(true);
    const [bottoming, setBottoming] = useState(false);
    const [scrollable, setScrollable] = useState(false);
    const updateScrollStateRef = useRef(null);
    const updateScrollState = hooks.useEventCallback(() => {
        const { scrollTop, scrollHeight, clientHeight, scrollWidth, clientWidth } = rootRef.current;
        let _topping;
        let _bottoming;
        let _scrollable;
        if (!horizontal) {
            _topping = scrollTop <= 1;
            _bottoming = Math.abs(scrollTop - (scrollHeight - clientHeight)) <= 1;
            _scrollable = scrollHeight > clientHeight;
        }
        else {
            const scrollLeft = getNormalizedScrollLeft(rootRef.current, isRTL);
            // use 1 for the potential rounding error with browser zooms.
            _topping = isRTL ? Math.abs(scrollLeft - (scrollLeft - clientWidth)) <= 1 : scrollLeft <= 1;
            _bottoming = !isRTL ? Math.abs(scrollLeft - (scrollLeft - clientWidth)) <= 1 : scrollLeft <= 1;
            _scrollable = scrollWidth > clientWidth;
        }
        _topping !== topping && setTopping(_topping);
        _bottoming !== bottoming && setBottoming(_bottoming);
        _scrollable !== scrollable && setScrollable(_scrollable);
    });
    useEffect(() => {
        updateScrollStateRef.current = lodash.debounce(updateScrollState, 300);
        return () => {
            var _a;
            (_a = updateScrollStateRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
        };
    }, [updateScrollState]);
    // When version changed, update scroll state
    hooks.useUpdateEffect(() => {
        updateScrollState();
    }, [updateScrollState, version]);
    const scroll = (previous, duration = 200) => {
        const delta = rootRef.current[clientSize] * (previous ? -1 : 1);
        let scrollValue = rootRef.current[scrollStart];
        if (!horizontal) {
            scrollValue += delta;
        }
        else {
            scrollValue += delta * (isRTL ? -1 : 1);
            // Fix for Edge
            // scrollValue *= isRTL && detectScrollType() === 'reverse' ? -1 : 1;
        }
        animate(scrollStart, rootRef.current, scrollValue, { duration }, updateScrollState);
    };
    useImperativeHandle(ref, () => ({
        scrollable,
        scroll,
        topping,
        bottoming,
        ref: rootRef
    }));
    const handleWhell = (evt) => {
        if (!horizontal)
            return;
        const previous = evt.deltaY < 0;
        if (topping && previous)
            return;
        if (bottoming && !previous)
            return;
        scroll(previous);
    };
    return (jsx("div", Object.assign({ css: style, className: classNames('scroll', className) }, others, { onWheel: handleWhell }),
        jsx("div", { className: 'scroll-root', ref: rootRef, onScroll: updateScrollStateRef.current }, children)));
});
